import type { TranscriptElement } from '@src/types.ts/transcript';

export interface TranscriptParagraphGroup {
  paragraphId: string | undefined;
  speakerLabel: string | undefined;
  isNewSpeaker: boolean;
  startTime: number | undefined;
  elements: Array<{ element: TranscriptElement; index: number }>;
}

/**
 * Check if paragraph_id values provide meaningful grouping.
 * Returns false when there are no paragraph_ids or when the average
 * words-per-paragraph is below 3 (indicates meaningless fragmentation).
 */
export function hasMeaningfulParagraphs(
  elements: TranscriptElement[],
): boolean {
  const textElements = elements.filter(el => el.type === 'text');
  const withId = textElements.filter(el => el.paragraph_id);
  if (withId.length === 0) return false;

  const uniqueIds = new Set(withId.map(el => el.paragraph_id));
  return withId.length / uniqueIds.size >= 3;
}

/**
 * Group transcript elements by paragraph boundaries (paragraph_id + newline chars)
 * and speaker changes.
 *
 * This is the single source of truth for paragraph grouping used by both
 * the transcript UI (TranscriptionText) and the export/copy feature.
 */
export function groupElementsByParagraph(
  elements: TranscriptElement[] | undefined | null,
): TranscriptParagraphGroup[] {
  if (!elements || elements.length === 0) return [];

  const meaningful = hasMeaningfulParagraphs(elements);

  // When paragraphs aren't meaningful, still split on speaker changes
  if (!meaningful) {
    return groupBySpeakerOnly(elements);
  }

  const groups: TranscriptParagraphGroup[] = [];
  let currentGroup: TranscriptParagraphGroup | null = null;
  let lastTextParagraphId: string | undefined = undefined;
  let lastTextSpeakerLabel: string | undefined = undefined;
  let previousGroupSpeakerLabel: string | undefined = undefined;
  let startNewGroupAfterNewline = false;

  elements.forEach((el, index) => {
    const paragraphId =
      el.type === 'text' ? el.paragraph_id : lastTextParagraphId;
    const speakerLabel =
      el.type === 'text' ? el.speaker_label : lastTextSpeakerLabel;

    if (el.type === 'text') {
      lastTextParagraphId = el.paragraph_id;
      lastTextSpeakerLabel = el.speaker_label;
    }

    const shouldStartNewGroup =
      !currentGroup ||
      currentGroup.paragraphId !== paragraphId ||
      currentGroup.speakerLabel !== speakerLabel ||
      (startNewGroupAfterNewline && el.type === 'text');

    if (shouldStartNewGroup) {
      const isNewSpeaker = speakerLabel !== previousGroupSpeakerLabel;
      const elementStartTime =
        el.type === 'text' ? (el.ts ?? undefined) : undefined;

      currentGroup = {
        paragraphId,
        speakerLabel,
        isNewSpeaker,
        startTime: elementStartTime,
        elements: [],
      };
      groups.push(currentGroup);

      previousGroupSpeakerLabel = speakerLabel;
      startNewGroupAfterNewline = false;
    }

    if (
      currentGroup &&
      currentGroup.startTime === undefined &&
      el.type === 'text'
    ) {
      currentGroup.startTime = el.ts ?? undefined;
    }

    if (currentGroup) {
      currentGroup.elements.push({ element: el, index });
    }

    if (el.value === '\n') {
      startNewGroupAfterNewline = true;
    }
  });

  return groups;
}

/**
 * Find the next paragraph group where the given speaker is speaking,
 * relative to the current playback time. Wraps to the first instance
 * if currently at or past the last instance.
 */
export function findNextSpeakerSection(
  groups: TranscriptParagraphGroup[],
  speakerLabel: string,
  currentTime: number,
): TranscriptParagraphGroup | undefined {
  const speakerGroups = groups.filter(
    g => g.speakerLabel === speakerLabel && g.startTime != null,
  );
  if (speakerGroups.length === 0) return undefined;

  // Use a small buffer so clicking "next" when already at a section's start advances past it
  const next = speakerGroups.find(g => (g.startTime ?? 0) > currentTime + 0.1);
  // Wrap to first section if past the last one
  return next ?? speakerGroups[0];
}

/**
 * Find the previous paragraph group where the given speaker is speaking,
 * relative to the current playback time. Wraps to the last instance
 * if currently at or before the first instance.
 */
export function findPreviousSpeakerSection(
  groups: TranscriptParagraphGroup[],
  speakerLabel: string,
  currentTime: number,
): TranscriptParagraphGroup | undefined {
  const speakerGroups = groups.filter(
    g => g.speakerLabel === speakerLabel && g.startTime != null,
  );
  if (speakerGroups.length === 0) return undefined;

  // Find the last section before the current time (with a small buffer to avoid landing on the same one)
  const reversed = [...speakerGroups].reverse();
  const prev = reversed.find(g => (g.startTime ?? 0) < currentTime - 0.5);
  // Wrap to last section if before the first one
  return prev ?? speakerGroups[speakerGroups.length - 1];
}

/**
 * Find the speaker section the user is currently within, or the next one
 * if not currently in any section for this speaker. Used for row clicks
 * (as opposed to explicit prev/next navigation).
 */
export function findCurrentOrNextSpeakerSection(
  groups: TranscriptParagraphGroup[],
  speakerLabel: string,
  currentTime: number,
): TranscriptParagraphGroup | undefined {
  const speakerGroups = groups.filter(
    g => g.speakerLabel === speakerLabel && g.startTime != null,
  );
  if (speakerGroups.length === 0) return undefined;

  // Check if currently within a speaker section
  for (let i = 0; i < speakerGroups.length; i++) {
    const g = speakerGroups[i];
    const nextG = speakerGroups[i + 1];
    const start = g.startTime ?? 0;
    if (
      start <= currentTime &&
      (!nextG || (nextG.startTime ?? 0) > currentTime)
    ) {
      return g;
    }
  }

  // Not within any section — find the next one after current time
  const next = speakerGroups.find(g => (g.startTime ?? 0) > currentTime);
  return next ?? speakerGroups[0];
}

/**
 * Group transcript elements by speaker_label changes only.
 * Used as a fallback when paragraph_ids aren't meaningful, and also
 * as a building block for speech-pace calculations.
 */
export function groupBySpeakerOnly(
  elements: TranscriptElement[],
): TranscriptParagraphGroup[] {
  const groups: TranscriptParagraphGroup[] = [];
  let currentGroup: TranscriptParagraphGroup | null = null;
  let lastTextSpeakerLabel: string | undefined = undefined;
  let previousGroupSpeakerLabel: string | undefined = undefined;

  elements.forEach((el, index) => {
    const speakerLabel =
      el.type === 'text' ? el.speaker_label : lastTextSpeakerLabel;

    if (el.type === 'text') {
      lastTextSpeakerLabel = el.speaker_label;
    }

    const shouldStartNewGroup =
      !currentGroup || currentGroup.speakerLabel !== speakerLabel;

    if (shouldStartNewGroup) {
      const isNewSpeaker = speakerLabel !== previousGroupSpeakerLabel;
      const elementStartTime =
        el.type === 'text' ? (el.ts ?? undefined) : undefined;

      currentGroup = {
        paragraphId: undefined,
        speakerLabel,
        isNewSpeaker,
        startTime: elementStartTime,
        elements: [],
      };
      groups.push(currentGroup);

      previousGroupSpeakerLabel = speakerLabel;
    }

    if (
      currentGroup &&
      currentGroup.startTime === undefined &&
      el.type === 'text'
    ) {
      currentGroup.startTime = el.ts ?? undefined;
    }

    if (currentGroup) {
      currentGroup.elements.push({ element: el, index });
    }
  });

  return groups;
}

// --- Speech pace calculation utilities ---

export interface SpeechSegment {
  speakerLabel: string | undefined;
  wordCount: number;
  /** Duration in seconds (first word ts → last word end_ts, including short intra-segment pauses) */
  duration: number;
}

const LONG_PAUSE_THRESHOLD_SECONDS = 1.0;

/**
 * Split a speaker group into sub-segments whenever a pause > 1 s occurs
 * between consecutive text elements. Each sub-segment captures the word
 * count and the wall-clock duration (first word start → last word end),
 * so short pauses within a segment are included but long silences are not.
 */
function splitGroupByPauses(group: TranscriptParagraphGroup): SpeechSegment[] {
  const segments: SpeechSegment[] = [];

  let segmentStart: number | null = null;
  let segmentEnd: number | null = null;
  let segmentWordCount = 0;

  for (const { element } of group.elements) {
    if (
      element.type !== 'text' ||
      element.state === 'removed' ||
      element.state === 'cut' ||
      element.ts == null ||
      element.end_ts == null
    ) {
      continue;
    }

    // Detect a long pause → close current segment and start a new one
    if (
      segmentEnd != null &&
      element.ts - segmentEnd > LONG_PAUSE_THRESHOLD_SECONDS
    ) {
      if (segmentStart != null && segmentWordCount > 0) {
        segments.push({
          speakerLabel: group.speakerLabel,
          wordCount: segmentWordCount,
          duration: segmentEnd - segmentStart,
        });
      }
      segmentStart = null;
      segmentEnd = null;
      segmentWordCount = 0;
    }

    if (segmentStart == null) {
      segmentStart = element.ts;
    }
    segmentEnd = element.end_ts;
    segmentWordCount++;
  }

  // Close the final segment
  if (segmentStart != null && segmentEnd != null && segmentWordCount > 0) {
    segments.push({
      speakerLabel: group.speakerLabel,
      wordCount: segmentWordCount,
      duration: segmentEnd - segmentStart,
    });
  }

  return segments;
}

export interface SpeechPaceResult {
  /** Words-per-second for each speaker (key = speaker_label or `'_unknown_'`). */
  perSpeaker: Map<string, number>;
  /**
   * Duration-weighted average pace (words/sec) across selected speakers.
   * 0 when no speech data is available.
   */
  average: number;
}

/**
 * Calculate speech pace (words per second) both per-speaker and as a
 * duration-weighted average for the selected speakers.
 *
 * Groups elements by speaker, splits each speaker group at long pauses
 * (> 1 s), and aggregates words / duration.
 *
 * @param speakerLabels - When non-empty only these speakers contribute to
 *   `average`. `perSpeaker` always contains **all** speakers regardless.
 */
export function calculateSpeechPace(
  elements: TranscriptElement[],
  speakerLabels?: string[],
): SpeechPaceResult {
  const speakerGroups = groupBySpeakerOnly(elements);
  const filterLabels =
    speakerLabels && speakerLabels.length > 0 ? new Set(speakerLabels) : null;

  // Accumulate totals per speaker across all their groups
  const totals = new Map<string, { words: number; duration: number }>();

  for (const group of speakerGroups) {
    const segments = splitGroupByPauses(group);
    for (const seg of segments) {
      const key = seg.speakerLabel ?? '_unknown_';
      const prev = totals.get(key) ?? { words: 0, duration: 0 };
      prev.words += seg.wordCount;
      prev.duration += seg.duration;
      totals.set(key, prev);
    }
  }

  // Per-speaker pace (all speakers)
  const perSpeaker = new Map<string, number>();
  for (const [speaker, { words, duration }] of totals) {
    if (duration > 0) {
      perSpeaker.set(speaker, words / duration);
    }
  }

  // Duration-weighted average for selected speakers (or all if none selected)
  let selectedWords = 0;
  let selectedDuration = 0;
  for (const [speaker, { words, duration }] of totals) {
    if (filterLabels && !filterLabels.has(speaker)) continue;
    selectedWords += words;
    selectedDuration += duration;
  }

  const average = selectedDuration > 0 ? selectedWords / selectedDuration : 0;

  return { perSpeaker, average };
}
